import { time } from '@/lib/time'
import { SALARY_WORK_TIME_QUERY } from '@/modules/companies/queries'
import type { SalaryModuleConfigNode } from '@/modules/companies/types.graphql'
import { SalaryType } from '@/modules/companies/types.graphql'
import { FixedHourlyAddons } from '@/modules/salary/FixedHourlyAddons'
import { useBreadcrumbs, useParam } from '@/utils/hooks'
import { getFullMinutes } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { Loader } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { format, setSeconds } from 'date-fns'
import floor from 'lodash/floor'
import { Fragment, useEffect, useEffectEvent, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useSalaryModuleTranslations } from '../../hooks'
import { useSalaryMutations } from '../../util'
import type { SalarySettingsForm } from './SalarySettingsFormView'
import { SalarySettingsFormView } from './SalarySettingsFormView'
import { H2 } from '@/components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

interface EditSalaryWorkTimeProps {}

export const EditSalaryWorkTime: React.FC<EditSalaryWorkTimeProps> = () => {
  const translations = useTranslate({
    editSalarySettings: ['settings.edit-salary-settings-type', { module: '' }],
    createSalarySettings: ['settings.create-salary-settings', { module: '' }],
    otherAddons: 'settings.other-addons',

    validation: {
      driverPause: 'settings.validation.salary-driver-pause-length',
      invalid: 'common.invalid',
      priority: ['settings.validation.salary-priority-unique', { module: '' }],
      required: 'common.required',
    },
  })

  const navigate = useNavigate()

  const getModuleTranslation = useSalaryModuleTranslations()

  const salaryWorkTimeId = useParam('salaryWorkTimeId')
  const [loadComplete, setLoadComplete] = useState(false)
  const { data, loading } = useQuery(SALARY_WORK_TIME_QUERY, {
    variables: {
      id: salaryWorkTimeId,
    },
    onCompleted: data => {
      const initialData = {
        userId: data.salaryWorkTime.user?.id ?? '',
        salaryGroups:
          data.salaryWorkTime.salaryGroups?.edges.map(
            edge => edge?.node?.id ?? ''
          ) ?? [],
        defaultSalaryCode: data.salaryWorkTime.defaultSalaryCode?.id ?? null,
        drivingSalaryCode: data.salaryWorkTime.drivingSalaryCode?.id ?? null,
        otherWorkSalaryCode:
          data.salaryWorkTime.otherWorkSalaryCode?.id ?? null,
        availabilitySalaryCode:
          data.salaryWorkTime.availabilitySalaryCode?.id ?? null,
        salaryType: data.salaryWorkTime.salaryType ?? SalaryType.HOUR,
        salaryDayCode: data.salaryWorkTime.salaryDayCode?.id ?? null,
        dayOvertimeSalaryCode:
          data.salaryWorkTime.dayOvertimeSalaryCode?.id ?? null,
        weekOvertimeSalaryCode:
          data.salaryWorkTime.weekOvertimeSalaryCode?.id ?? null,
        priority: data.salaryWorkTime.priority ?? 1,
        dailyWorkLimit: floor(
          (data.salaryWorkTime.dailyWorkLimit ?? 28800) / 3600,
          2
        ),
        periodWorkLimit: floor(
          (data.salaryWorkTime.periodWorkLimit ?? 144000) / 3600,
          2
        ),
        pauseDuration: secondsToDate(data.salaryWorkTime.pauseDuration ?? 2700),
        pauseThreshold: secondsToDate(
          data.salaryWorkTime.pauseThreshold ?? 14400
        ),
        manuallyAddPaidPauseForThresholds:
          data.salaryWorkTime.manuallyAddPaidPauseForThresholds ?? false,
        paidPauseSalaryCode:
          data.salaryWorkTime.paidPauseSalaryCode?.id ?? null,
        countPauseInTotal: data.salaryWorkTime.countPauseInTotal ?? false,
        paidPauseThreshold: secondsToDate(
          data.salaryWorkTime.paidPauseThreshold ?? 0
        ),
        salaryDateStart: data.salaryWorkTime.salaryDateStart ?? 26,
        salaryDateEnd: data.salaryWorkTime.salaryDateEnd ?? 25,
        name: data.salaryWorkTime.name ?? '',
        usePeriodOvertime: data.salaryWorkTime.usePeriodOvertime ?? false,
        useSalaryDayPayment: data.salaryWorkTime.useSalaryDayPayment ?? false,
        useWeekOvertime: data.salaryWorkTime.useWeekOvertime ?? true,
        calculateWeekOvertimeInline:
          data.salaryWorkTime.calculateWeekOvertimeInline ?? true,
        useMonthOvertime: data.salaryWorkTime.useMonthOvertime ?? false,
        monthOvertimeSalaryCode:
          data.salaryWorkTime.monthOvertimeSalaryCode?.id ?? null,
        groupSalaryOnVehicle: data.salaryWorkTime.groupSalaryOnVehicle ?? false,
        useAutomaticDietGeneration:
          data.salaryWorkTime.useAutomaticDietGeneration ?? false,
        useAutomaticSalaryDayGeneration:
          data.salaryWorkTime.useAutomaticSalaryDayGeneration ?? false,
        periodAddons:
          data.salaryWorkTime.periodAddons.map(periodAddonNode => {
            const { __typename, ...periodAddon } = periodAddonNode
            return {
              ...periodAddon,
              salaryCode: periodAddon.salaryCode?.id ?? '',
              start: time(periodAddon.start).toDate(new Date()),
              end: time(periodAddon.end).toDate(new Date()),
              salaryCodeName: periodAddon.salaryCode?.name ?? '',
            }
          }) ?? [],
        defaultDepartment: data.salaryWorkTime.defaultDepartment?.id ?? '',
        availableDepartments:
          data.salaryWorkTime.availableDepartments?.map(node => node.id) ?? [],
        availableAddons:
          data.salaryWorkTime.selectableSalaryCodes?.map(node => node.id) ?? [],
        holidayAllowances:
          data.salaryWorkTime.holidayAllowances?.map(node => node.id) ?? [],
      }
      form.updateForm(initialData)
      setLoadComplete(true)
    },
    onError() {
      void navigate(-1)
    },
  })
  const salaryWorkTime = data?.salaryWorkTime
  const config = salaryWorkTime?.moduleConfig

  const [breadcrumbsOverridden, setBreadcrumbsOverridden] = useState(false)
  const { setOverride } = useBreadcrumbs()

  const mutations = useSalaryMutations()

  const secondsToDate = (seconds: number) =>
    setSeconds(new Date(1993, 0, 9), seconds)

  const form = useForm<SalarySettingsForm>({
    values: {
      userId: salaryWorkTime?.user?.id ?? '',
      salaryGroups:
        salaryWorkTime?.salaryGroups?.edges.map(edge => edge?.node?.id ?? '') ??
        [],

      salaryType: salaryWorkTime?.salaryType ?? SalaryType.HOUR,
      defaultSalaryCode: salaryWorkTime?.defaultSalaryCode?.id ?? '',
      salaryDayCode: salaryWorkTime?.salaryDayCode?.id ?? '',
      dayOvertimeSalaryCode: salaryWorkTime?.dayOvertimeSalaryCode?.id ?? '',

      useWeekOvertime: salaryWorkTime?.useWeekOvertime ?? true,
      calculateWeekOvertimeInline:
        salaryWorkTime?.calculateWeekOvertimeInline ?? true,
      weekOvertimeSalaryCode: salaryWorkTime?.weekOvertimeSalaryCode?.id ?? '',

      useMonthOvertime: salaryWorkTime?.useMonthOvertime ?? false,
      monthOvertimeSalaryCode:
        salaryWorkTime?.monthOvertimeSalaryCode?.id ?? '',

      dailyWorkLimit: floor(
        (salaryWorkTime?.dailyWorkLimit ?? 28800) / 3600,
        2
      ),
      periodWorkLimit: floor(
        (salaryWorkTime?.periodWorkLimit ?? 144000) / 3600,
        2
      ),

      pauseDuration: secondsToDate(salaryWorkTime?.pauseDuration ?? 2700),
      pauseThreshold: secondsToDate(salaryWorkTime?.pauseThreshold ?? 14400),
      countPauseInTotal: salaryWorkTime?.countPauseInTotal ?? false,
      paidPauseThreshold: secondsToDate(
        salaryWorkTime?.paidPauseThreshold ?? 0
      ),
      manuallyAddPaidPauseForThresholds:
        salaryWorkTime?.manuallyAddPaidPauseForThresholds ?? false,
      paidPauseSalaryCode: salaryWorkTime?.paidPauseSalaryCode?.id ?? '',
      drivingSalaryCode: salaryWorkTime?.drivingSalaryCode?.id ?? '',
      otherWorkSalaryCode: salaryWorkTime?.otherWorkSalaryCode?.id ?? '',
      availabilitySalaryCode: salaryWorkTime?.availabilitySalaryCode?.id ?? '',
      salaryDateStart: salaryWorkTime?.salaryDateStart ?? 26,
      salaryDateEnd: salaryWorkTime?.salaryDateEnd ?? 25,
      name: salaryWorkTime?.name ?? '',
      groupSalaryOnVehicle: salaryWorkTime?.groupSalaryOnVehicle ?? false,
      usePeriodOvertime: false,
      useSalaryDayPayment: false,

      periodAddons:
        salaryWorkTime?.periodAddons.map(periodAddonNode => {
          const { __typename, ...periodAddon } = periodAddonNode
          return {
            ...periodAddon,
            salaryCode: periodAddon.salaryCode?.id ?? '',
            start: time(periodAddon.start).toDate(new Date()),
            end: time(periodAddon.end).toDate(new Date()),
            salaryCodeName: periodAddon.salaryCode?.name ?? '',
          }
        }) ?? [],
      defaultDepartment: salaryWorkTime?.defaultDepartment?.id ?? '',
      availableDepartments:
        salaryWorkTime?.availableDepartments?.map(node => node.id) ?? [],
      availableAddons:
        salaryWorkTime?.selectableSalaryCodes?.map(node => node.id) ?? [],
      useAutomaticDietGeneration:
        salaryWorkTime?.useAutomaticDietGeneration ?? false,
      useAutomaticSalaryDayGeneration:
        salaryWorkTime?.useAutomaticSalaryDayGeneration ?? false,
      holidayAllowances:
        salaryWorkTime?.holidayAllowances?.map(node => node.id) ?? [],
    },
    validators: {
      salaryDateStart: (val, { salaryDateEnd }) =>
        val === 1 && salaryDateEnd === 31
          ? null
          : val > salaryDateEnd
          ? null
          : translations.validation.invalid,
    },
    config: {
      validateImmediately: true,
    },
  })

  async function handleEdit() {
    void mutations.patch({
      variables: {
        id: data?.salaryWorkTime.id ?? '',
        input: {
          user: form.formValues.userId,
          salaryGroups: form.formValues.salaryGroups,
          salaryType: form.formValues.salaryType,
          defaultSalaryCode: form.formValues.defaultSalaryCode,
          drivingSalaryCode: form.formValues.drivingSalaryCode,
          otherWorkSalaryCode: form.formValues.otherWorkSalaryCode,
          availabilitySalaryCode: form.formValues.availabilitySalaryCode,
          salaryDayCode: form.formValues.salaryDayCode,
          dayOvertimeSalaryCode: form.formValues.dayOvertimeSalaryCode,
          useWeekOvertime: form.formValues.useWeekOvertime,
          calculateWeekOvertimeInline:
            form.formValues.calculateWeekOvertimeInline,
          weekOvertimeSalaryCode: form.formValues.weekOvertimeSalaryCode,
          useMonthOvertime: form.formValues.useMonthOvertime,
          monthOvertimeSalaryCode: form.formValues.monthOvertimeSalaryCode,
          dailyWorkLimit: form.formValues.dailyWorkLimit,
          periodWorkLimit: form.formValues.periodWorkLimit,
          pauseDuration: getFullMinutes(form.formValues.pauseDuration),
          pauseThreshold: getFullMinutes(form.formValues.pauseThreshold),
          countPauseInTotal: form.formValues.countPauseInTotal,
          paidPauseThreshold: getFullMinutes(
            form.formValues.paidPauseThreshold
          ),
          manuallyAddPaidPauseForThresholds:
            form.formValues.manuallyAddPaidPauseForThresholds,
          paidPauseSalaryCode: form.formValues.paidPauseSalaryCode,
          salaryDateStart: form.formValues.salaryDateStart,
          salaryDateEnd: form.formValues.salaryDateEnd,
          name: form.formValues.name,
          groupSalaryOnVehicle: form.formValues.groupSalaryOnVehicle,
          usePeriodOvertime: form.formValues.usePeriodOvertime,
          useSalaryDayPayment: form.formValues.useSalaryDayPayment,
          useAutomaticDietGeneration:
            form.formValues.useAutomaticDietGeneration,
          useAutomaticSalaryDayGeneration:
            form.formValues.useAutomaticSalaryDayGeneration,
          periodAddonsManual: form.formValues.periodAddons.map(periodAddon => {
            const { id, salaryCodeName, ...periodAddonInput } = periodAddon
            return {
              ...periodAddonInput,
              start: format(periodAddon.start, 'HH:mm'),
              end: format(periodAddon.end, 'HH:mm'),
            }
          }),
          defaultDepartment: form.formValues.defaultDepartment,
          availableDepartments: form.formValues.availableDepartments,
          selectableSalaryCodes: form.formValues.availableAddons,
          holidayAllowances: form.formValues.holidayAllowances,
        },
      },
    })
  }

  const title = config?.name ? (
    translations.editSalarySettings({
      module: getModuleTranslation(config.name),
    })
  ) : (
    <Loader.Dots />
  )

  const handleBreadcrumbOverrides = useEffectEvent(() => {
    if (!salaryWorkTime?.user) return
    setOverride(salaryWorkTime.user.id, salaryWorkTime.user.fullName)
    setBreadcrumbsOverridden(true)
  })

  useEffect(() => {
    if (!breadcrumbsOverridden) handleBreadcrumbOverrides()
  }, [breadcrumbsOverridden])

  const UserWrapper = form.formValues.userId ? Wrapper : Fragment

  return (
    <UserWrapper>
      <H2>{title}</H2>

      {config && !loading && loadComplete && (
        <div>
          <SalarySettingsFormView
            form={form}
            // TODO: Fix this type assertion
            config={config as SalaryModuleConfigNode}
            onSubmit={handleEdit}
          />

          <h2>{translations.otherAddons}</h2>

          {!form.formValues.userId && (
            <FixedHourlyAddons
              type="salaryWorkTime"
              typeId={salaryWorkTime.id}
            />
          )}
        </div>
      )}
    </UserWrapper>
  )
}
